import React from 'react';
import { Modal, Typography, Divider, Tag, Alert } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SecurityScanOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

/**
 * ContentSecurityModal
 *
 * Displays comprehensive help documentation about content security
 * and sanitization in the Report Writer's rich text editor.
 */
const ContentSecurityModal = ({ visible, onClose }) => {
  return (
    <Modal
      title={
        <span>
          <SecurityScanOutlined style={{ marginRight: 8, color: 'var(--rlc-state-info)' }} />
          Content Security in Report Writer
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>

        {/* Overview */}
        <Alert
          message="Your content is automatically protected"
          description="RLC Spark sanitizes all pasted content to remove security risks while preserving your text and formatting."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* What's Allowed */}
        <Title level={4}>
          <CheckCircleOutlined style={{ color: 'var(--rlc-state-success)', marginRight: 8 }} />
          What Content is Allowed
        </Title>
        <Paragraph>
          <Text strong>99% of normal content works perfectly</Text> - including content from:
        </Paragraph>
        <ul style={{ marginBottom: 24 }}>
          <li><Text strong>Word & Google Docs:</Text> Bold, italic, underline, headings, lists</li>
          <li><Text strong>Excel & Google Sheets:</Text> Tables, formatted cells, charts (as images)</li>
          <li><Text strong>Email:</Text> Formatted text, links, simple styling</li>
          <li><Text strong>Websites:</Text> Text, paragraphs, headings, lists</li>
          <li><Text strong>Quill Toolbar:</Text> All formatting options available in the editor</li>
        </ul>

        <Paragraph>
          <Text strong>Supported HTML tags:</Text>
        </Paragraph>
        <div style={{ marginBottom: 24 }}>
          <Tag color="green">p</Tag>
          <Tag color="green">strong</Tag>
          <Tag color="green">em</Tag>
          <Tag color="green">u</Tag>
          <Tag color="green">h1-h6</Tag>
          <Tag color="green">ul</Tag>
          <Tag color="green">ol</Tag>
          <Tag color="green">li</Tag>
          <Tag color="green">a</Tag>
          <Tag color="green">table</Tag>
          <Tag color="green">img</Tag>
          <Tag color="green">div</Tag>
          <Tag color="green">span</Tag>
          <Tag color="green">blockquote</Tag>
        </div>

        <Divider />

        {/* What's Blocked */}
        <Title level={4}>
          <CloseCircleOutlined style={{ color: 'var(--rlc-state-error)', marginRight: 8 }} />
          What Content is Blocked (For Security)
        </Title>
        <Paragraph>
          <Text strong>Only dangerous/malicious content is removed:</Text>
        </Paragraph>
        <ul style={{ marginBottom: 24 }}>
          <li>
            <Text strong>Scripts:</Text> <Tag color="red">{'<script>'}</Tag> -
            Can execute malicious code in your browser
          </li>
          <li>
            <Text strong>Embedded frames:</Text> <Tag color="red">{'<iframe>'}</Tag> -
            Can load malicious websites
          </li>
          <li>
            <Text strong>Event handlers:</Text> <Tag color="red">onclick=</Tag> <Tag color="red">onerror=</Tag> -
            Can trigger malicious actions
          </li>
          <li>
            <Text strong>Embedded objects:</Text> <Tag color="red">{'<object>'}</Tag> <Tag color="red">{'<embed>'}</Tag> -
            Can load unsafe plugins
          </li>
          <li>
            <Text strong>JavaScript URLs:</Text> <Tag color="red">javascript:</Tag> -
            Can execute code when clicked
          </li>
        </ul>

        <Alert
          message="Important: These are NOT features of the Report Writer"
          description="The blocked content types are not supported features anyway - they are security risks that should never appear in reports."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Divider />

        {/* Examples */}
        <Title level={4}>Real-World Examples</Title>

        <Paragraph>
          <Text strong style={{ color: 'var(--rlc-state-success)' }}>✓ Example 1: Normal Report Writing</Text>
        </Paragraph>
        <Paragraph style={{ marginLeft: 16, marginBottom: 16 }}>
          <Text type="secondary">You paste from Word:</Text><br />
          <Text code>"Our Q3 results show 25% growth"</Text><br />
          <Text type="secondary">Result:</Text> <CheckCircleOutlined style={{ color: 'var(--rlc-state-success)' }} /> Works perfectly - no changes
        </Paragraph>

        <Paragraph>
          <Text strong style={{ color: 'var(--rlc-state-success)' }}>✓ Example 2: Excel Table</Text>
        </Paragraph>
        <Paragraph style={{ marginLeft: 16, marginBottom: 16 }}>
          <Text type="secondary">You copy cells from Excel:</Text><br />
          <Text code>Product | Sales | Growth</Text><br />
          <Text type="secondary">Result:</Text> <CheckCircleOutlined style={{ color: 'var(--rlc-state-success)' }} /> Table inserted with clean formatting
        </Paragraph>

        <Paragraph>
          <Text strong style={{ color: 'var(--rlc-state-error)' }}>⚠ Example 3: Malicious Content</Text>
        </Paragraph>
        <Paragraph style={{ marginLeft: 16, marginBottom: 16 }}>
          <Text type="secondary">You paste from unknown source:</Text><br />
          <Text code>{'<div>Report<script>steal()</script></div>'}</Text><br />
          <Text type="secondary">Result:</Text> Script removed, "Report" preserved<br />
          <Text type="secondary">Notification:</Text> "Content security: Removed scripts for safety"
        </Paragraph>

        <Divider />

        {/* Why This Exists */}
        <Title level={4}>Why This Protection Exists</Title>
        <Paragraph>
          Content security protects you and your organization from:
        </Paragraph>
        <ul style={{ marginBottom: 24 }}>
          <li><Text strong>Cross-Site Scripting (XSS) attacks:</Text> Malicious code that could steal your data</li>
          <li><Text strong>Phishing attempts:</Text> Embedded content that tricks you into sharing credentials</li>
          <li><Text strong>Data theft:</Text> Scripts that could send your reports to attackers</li>
          <li><Text strong>Session hijacking:</Text> Code that could take over your login session</li>
        </ul>

        <Alert
          message="Multi-Tenant Protection"
          description="Content security also ensures that reports from one organization cannot affect another organization's data - maintaining strict tenant isolation."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Divider />

        {/* What You See */}
        <Title level={4}>When Will I See Notifications?</Title>
        <Paragraph>
          You'll only see security notifications if:
        </Paragraph>
        <ol style={{ marginBottom: 24 }}>
          <li>You paste content containing scripts, iframes, or event handlers</li>
          <li>The content was actually modified for security</li>
          <li>This is extremely rare for normal use (Word, Excel, email)</li>
        </ol>

        <Paragraph>
          <Text strong>Most users never see these notifications</Text> because normal office
          documents don't contain dangerous code.
        </Paragraph>

        <Divider />

        {/* Need Help */}
        <Title level={4}>Need Help?</Title>
        <Paragraph>
          If content you expect to work is being blocked:
        </Paragraph>
        <ul>
          <li>Check your browser console (F12) for detailed logs</li>
          <li>Try pasting as plain text first, then apply formatting</li>
          <li>Contact your administrator if legitimate content is blocked</li>
        </ul>

        <Alert
          message="Security is a Feature, Not a Bug"
          description="These protections keep your data safe while allowing all normal report writing workflows to work seamlessly."
          type="success"
          showIcon
        />
      </div>
    </Modal>
  );
};

export default ContentSecurityModal;
